// @@POS_REPORTS_v104@@ HQ — POS daily sales (Square daily summary emails) per lease,
// one draft Zoho credit note per day, and the lease settlement (charges invoice).
// All numbers come from the server (/pos/preview, saved rows); this file only displays them.
// @@POS_TENDER_v104_1@@ Square Sales summary CSV (card/cash split, one day or a date range).
// @@POS_EDIT_v104_2@@ Edit / Delete on every report row (lease page AND the POS sales tab).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { LINK, MUTED, LABEL, TEXTAREA, GHOST, pill, money, fmtYmd, fmtTs, readJson, JSON_HEADERS } from '../lib/equipment';

const FULL = { width: '100%', boxSizing: 'border-box' };
const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const GREEN_BTN = { background: '#31b44b', color: '#0a0a0b' };
const BOX = { border: '1px solid var(--hq-line)', borderRadius: 12, padding: 16 };
const num = (v) => (v == null ? '' : String(v));
const span = (r) => (r.period_end ? `${fmtYmd(r.report_date)} – ${fmtYmd(r.period_end)}` : fmtYmd(r.report_date));

export const BLANK_DAY = {
  report_date: '', location_name: '', gross_sales: '', service_charges: '', returns: '', discounts: '', net_sales: '',
  square_tax: '', tips: '', card_fees: '', total_collected: '', items_sold: '', customers: '', fee_detail: '',
  period_end: '', card_collected: '', cash_collected: '', other_collected: '',
  tax_state: '', tax_rate: '', client_note: '', notes: '', source: 'manual', top_items: [],
};
// Parsed email fields / a saved report -> form strings.
export function dayFromFields(f, source) {
  const out = { ...BLANK_DAY, source: source || 'manual' };
  for (const k of Object.keys(BLANK_DAY)) if (f && f[k] != null && k !== 'top_items' && k !== 'source') out[k] = num(f[k]);
  if (f && Array.isArray(f.top_items)) out.top_items = f.top_items;
  if (out.tax_rate === '0') out.tax_rate = '';
  return out;
}

function StatusCell({ r }) {
  if (r.status === 'draft') return <span style={pill('grey')}>Draft</span>;
  if (r.cn_status === 'created') return <span><span style={pill('green')}>Credited</span><div style={{ ...MUTED, ...MONO, whiteSpace: 'nowrap' }} title={r.cn_number}>{String(r.cn_number || '').replace(/\s*\(.*\)$/, '')}</div></span>;
  if (r.cn_status === 'creating') return <span style={pill(r.cn_stuck ? 'red' : 'amber')}>{r.cn_stuck ? 'CN stuck' : 'Creating CN…'}</span>;
  if (Number(r.amount_due) <= 0) return <span style={pill('amber')}>Final · nothing due</span>;
  return <span style={pill('blue')}>Final · no CN</span>;
}

// @@POS_EDIT_v104_2@@ Row actions shared by the lease panel and the POS sales tab.
// Edit / Delete work on any report without a credit note (draft or final). A credited
// report can be edited or deleted only after you confirm its credit note was voided in Zoho.
export function ReportActions({ r, zohoLinked, busy, act, onEdit }) {
  const credited = r.cn_status === 'created';
  const voidOk = () => window.confirm(`${span(r)} has credit note ${r.cn_number || ''} in Zoho.\n\nVoid (or delete) that credit note in Zoho first. Have you done that?`);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {r.status === 'final' && !r.cn_status && Number(r.amount_due) > 0 && zohoLinked &&
        <button className="hq-btn" style={GREEN_BTN} disabled={!!busy} onClick={() => act('POST', `/pos-reports/${r.id}/credit-note`, {}, 'cn' + r.id)}>{busy === 'cn' + r.id ? 'Creating…' : 'Create credit note'}</button>}
      {r.cn_status !== 'creating' && <button className="hq-signout" style={GHOST} disabled={!!busy}
        onClick={() => { if (!credited) onEdit(r, false); else if (voidOk()) onEdit(r, true); }}>Edit</button>}
      {r.cn_status !== 'creating' && <button className="hq-signout" style={GHOST} disabled={!!busy}
        onClick={() => {
          if (credited) { if (voidOk() && window.confirm(`Delete the report for ${span(r)}? This can’t be undone.`)) act('DELETE', `/pos-reports/${r.id}?voided=1`, null, 'del'); }
          else if (window.confirm(`Delete the ${r.status === 'final' ? 'final ' : 'draft '}report for ${span(r)}?${r.status === 'final' ? ' The client will no longer see it.' : ''}`)) act('DELETE', `/pos-reports/${r.id}`, null, 'del');
        }}>Delete</button>}
      {r.cn_stuck && <button className="hq-signout" style={GHOST} disabled={!!busy}
        onClick={() => { if (window.confirm('Check Zoho first: if a credit note for this report exists there, don’t reset. Reset the stuck claim?')) act('POST', `/pos-reports/${r.id}/credit-note/reset`, {}, 'reset'); }}>Reset</button>}
    </div>
  );
}

// ── drop / paste the Square email ────────────────────────────────────────────
export function PosEmailDrop({ onParsed, compact }) {
  const [over, setOver] = useState(false);
  const [paste, setPaste] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  async function send(text, filename) {
    if (!text || !text.trim()) return;
    setBusy(true); setErr('');
    const r = await apiFetch('/api/equipment/pos/parse' + (filename ? '?filename=' + encodeURIComponent(filename) : ''), { method: 'POST', headers: { 'Content-Type': 'text/plain; charset=utf-8' }, body: text });
    const d = await readJson(r);
    setBusy(false);
    if (!r.ok) { setErr(d.error || 'Could not read that email.'); return; }
    if (!d.parse.ok) { setErr((d.parse.warnings || []).join(' ') || 'That doesn’t look like a Square daily summary.'); return; }
    setPaste('');
    onParsed(d);
  }
  async function readFile(file) {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { setErr('That file is too big for a Square summary email.'); return; }
    send(await file.text(), file.name);
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); readFile(e.dataTransfer.files && e.dataTransfer.files[0]); }}
        onClick={() => fileRef.current && fileRef.current.click()} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current && fileRef.current.click(); } }}
        style={{ border: `1.5px dashed ${over ? '#31b44b' : 'var(--hq-line)'}`, borderRadius: 12, padding: compact ? '16px' : '26px 16px', textAlign: 'center', cursor: 'pointer', background: over ? 'rgba(49,180,75,.06)' : 'transparent' }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{busy ? 'Reading…' : 'Drop the Square Sales summary CSV here'}</div>
        <div style={MUTED}>Square › Reports › Sales summary › pick the day or date range › Export › Summary CSV. Click to pick the file. (The daily email .eml also works, but it doesn’t split card from cash.)</div>
        <input ref={fileRef} type="file" accept=".csv,text/csv,.eml,message/rfc822,.txt,.html,.htm" style={{ display: 'none' }} onChange={(e) => { readFile(e.target.files[0]); e.target.value = ''; }} />
      </div>
      <details>
        <summary style={{ ...MUTED, cursor: 'pointer' }}>…or paste the CSV / email text</summary>
        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <textarea style={{ ...TEXTAREA, minHeight: 110 }} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="Paste the CSV text, or the email text (include the subject line)." />
          <div><button type="button" className="hq-btn" disabled={busy || !paste.trim()} onClick={() => send(paste)}>Read pasted text</button></div>
        </div>
      </details>
      {err && <div style={{ color: '#ff7a73', fontSize: 13 }}>{err}</div>}
    </div>
  );
}

// ── one day's form, with the server's live preview ───────────────────────────
export function PosDayForm({ leaseId, leaseOptions, initialLease, initial, reportId, reportStatus, voided, leaseLocation, onDone, onCancel }) {
  const [lease, setLease] = useState(leaseId || initialLease || '');
  const [f, setF] = useState(initial || BLANK_DAY);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [remember, setRemember] = useState(false);
  const opt = (leaseOptions || []).find((o) => o.id === lease);
  const knownLoc = leaseId ? leaseLocation : opt && opt.pos_location;
  const locDiffers = !!f.location_name && String(knownLoc || '').trim().toLowerCase() !== f.location_name.trim().toLowerCase();
  useEffect(() => { setRemember(!reportId && locDiffers && !knownLoc); }, [lease, locDiffers, knownLoc, reportId]);

  const body = { ...f, lease_id: lease };
  const key = JSON.stringify(body);
  useEffect(() => {
    if (!lease) { setPreview(null); return undefined; }
    const t = setTimeout(async () => {
      const r = await apiFetch('/api/equipment/pos/preview', { method: 'POST', headers: JSON_HEADERS, body: key });
      const d = await readJson(r);
      setPreview(r.ok ? d : { ok: false, error: d.error || 'Preview unavailable.' });
    }, 300);
    return () => clearTimeout(t);
  }, [key, lease]);

  const set = (k) => (e) => setF({ ...f, [k]: k === 'tax_state' ? e.target.value.toUpperCase() : e.target.value });
  async function save(mode) {
    setBusy(mode); setErr('');
    const payload = { ...f, finalize: mode !== 'draft', credit_note: mode === 'cn', remember_location: remember, voided_credit_note: !!voided };
    const r = reportId
      ? await apiFetch('/api/equipment/pos-reports/' + reportId, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(payload) })
      : await apiFetch(`/api/equipment/leases/${lease}/pos-reports`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(payload) });
    const d = await readJson(r);
    setBusy('');
    if (!r.ok) {
      setErr(d.error || 'Could not save.');
      if (d.pos) onDone(d.pos, null, lease, true);
      return;
    }
    onDone(d.pos, d.credit_note_error ? `Saved and finalized, but the credit note wasn’t created: ${d.credit_note_error}` : d.message, lease, false);
  }

  const money3 = (k, label, sub) => (
    <label style={{ flex: '1 1 130px', minWidth: 0 }}><span style={LABEL}>{label}</span>
      <input className="hq-input" style={FULL} inputMode="decimal" value={f[k]} onChange={set(k)} aria-label={label} />
      {sub && <span style={MUTED}>{sub}</span>}</label>
  );
  const c = preview && preview.ok ? preview.computed : null;
  const pe = preview && preview.ok ? preview.period_error : null;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {!leaseId && (
          <label style={{ flex: '2 1 280px' }}><span style={LABEL}>Lease *</span>
            <select className="hq-select" style={{ width: '100%' }} value={lease} onChange={(e) => setLease(e.target.value)}>
              <option value="">Choose the lease…</option>
              {(leaseOptions || []).map((o) => (
                <option key={o.id} value={o.id}>{o.number} — {o.client_name}{o.event_name ? ' · ' + o.event_name : ''} ({fmtYmd(o.start_date)} – {fmtYmd(o.end_date)}){o.location_match ? ' ✓ location' : ''}{o.in_period ? '' : ' — outside period'}</option>
              ))}
            </select></label>
        )}
        <label style={{ flex: '1 1 160px' }}><span style={LABEL}>Sales date (from) *</span>
          <input type="date" className="hq-input" style={FULL} value={f.report_date} onChange={set('report_date')} /></label>
        <label style={{ flex: '1 1 160px' }}><span style={LABEL}>Through (multi-day CSV)</span>
          <input type="date" className="hq-input" style={FULL} value={f.period_end} onChange={set('period_end')} aria-label="Through date" /></label>
        <label style={{ flex: '1 1 180px' }}><span style={LABEL}>Square location</span>
          <input className="hq-input" style={FULL} value={f.location_name} onChange={set('location_name')} maxLength={120} placeholder="Goblin" /></label>
      </div>
      {locDiffers && lease && !reportId && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          Remember “{f.location_name}” as this lease’s Square location (future emails match automatically){knownLoc ? ` — currently “${knownLoc}”` : ''}
        </label>
      )}

      <div style={BOX}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>From Square</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {money3('gross_sales', 'Gross sales')}
          {money3('returns', 'Returns')}
          {money3('discounts', 'Discounts & comps')}
          {money3('service_charges', 'Service charges')}
          {money3('net_sales', 'Net sales *')}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          {money3('square_tax', 'Tax (Square)', 'Usually $0 — prices include tax')}
          {money3('tips', 'Tips')}
          {money3('card_fees', 'Card fees', 'Positive number')}
          {money3('total_collected', 'Total collected')}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          {money3('card_collected', 'Card payments', 'Settles through XYZ')}
          {money3('cash_collected', 'Cash payments', 'Kept by the client')}
          {money3('other_collected', 'Other tenders', 'Gift cards etc. (kept)')}
          <label style={{ flex: '1 1 90px' }}><span style={LABEL}>Items sold</span>
            <input className="hq-input" style={FULL} inputMode="numeric" value={f.items_sold} onChange={set('items_sold')} /></label>
          <label style={{ flex: '1 1 90px' }}><span style={LABEL}>Customers</span>
            <input className="hq-input" style={FULL} inputMode="numeric" value={f.customers} onChange={set('customers')} /></label>
        </div>
        {f.fee_detail && <div style={{ ...MUTED, marginTop: 8 }}>Square fee rates: {f.fee_detail}</div>}
        {(f.top_items || []).length > 0 && (
          <div style={{ ...MUTED, marginTop: 6 }}>Top sellers: {f.top_items.slice(0, 5).map((t) => `${t.name} ×${t.qty}`).join(' · ')}{f.top_items.length > 5 ? ` · +${f.top_items.length - 5} more` : ''}</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ flex: '0 1 110px' }}><span style={LABEL}>Tour stop state</span>
          <input className="hq-input" style={FULL} value={f.tax_state} onChange={set('tax_state')} maxLength={2} placeholder="CA" /></label>
        <label style={{ flex: '0 1 160px' }}><span style={LABEL}>Sales tax rate (%)</span>
          <input className="hq-input" style={FULL} inputMode="decimal" value={f.tax_rate} onChange={set('tax_rate')} placeholder="e.g. 9.5" /></label>
        <span style={{ ...MUTED, flex: '1 1 240px', paddingBottom: 8 }}>The stop’s combined rate. When Square’s tax line is $0, the tax is backed out of net sales at this rate.</span>
      </div>

      <div style={{ ...BOX, background: 'var(--hq-field)' }} aria-live="polite">
        {!lease && <div style={MUTED}>Pick the lease to see the settlement.</div>}
        {lease && preview && !preview.ok && <div style={{ color: '#e8c15a', fontSize: 13 }}>{preview.error}</div>}
        {c && (
          <div style={{ display: 'grid', gap: 6, fontSize: 14, maxWidth: 520 }}>
            <Row k="Card payments collected (settles through XYZ)" v={money(c.card_collected)} />
            {Number(c.cash_collected) + Number(c.other_collected) !== 0 && <div style={MUTED}>Cash{Number(c.other_collected) ? ' + other' : ''} kept by the client: {money(Number(c.cash_collected) + Number(c.other_collected))} — not settled through XYZ, but sales tax is due on it.</div>}
            <Row k={c.tax_mode === 'square' ? 'Sales tax on all sales (collected by Square)' : c.tax_mode === 'rate' ? `Sales tax on all sales${f.tax_state ? ' ' + f.tax_state : ''} ${f.tax_rate}% (included in prices)` : 'Sales tax'} v={'− ' + money(c.tax_deducted)} />
            <Row k="Card processing fees (Square, at cost)" v={'− ' + money(f.card_fees || 0)} />
            <Row k={`Event service fee ${c.service_fee_pct}% of card sales ${money(c.service_fee_base)}`} v={'− ' + money(c.service_fee)} />
            <Row k={<strong>Due to client</strong>} v={<strong style={{ color: Number(c.amount_due) > 0 ? '#5fdc97' : '#ff7a73' }}>{money(c.amount_due)}</strong>} top />
            {pe && <div style={{ color: '#ff7a73', fontSize: 13 }}>{pe}</div>}
            {c.warnings.map((w) => <div key={w} style={{ color: '#e8c15a', fontSize: 13 }}>{w}</div>)}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ flex: '1 1 280px' }}><span style={LABEL}>Note to the client (shown in their portal)</span>
          <input className="hq-input" style={FULL} value={f.client_note} onChange={set('client_note')} maxLength={1000} /></label>
        <label style={{ flex: '1 1 280px' }}><span style={LABEL}>Internal note</span>
          <input className="hq-input" style={FULL} value={f.notes} onChange={set('notes')} maxLength={5000} placeholder="Never shown to the client" /></label>
      </div>

      {voided && <div style={{ color: '#e8c15a', fontSize: 13 }}>Its old credit note is treated as voided in Zoho: saving clears the link, so you can create a new one.</div>}
      {err && <div style={{ color: '#ff7a73', fontSize: 13 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="hq-btn" style={GREEN_BTN} disabled={!!busy || !lease || !!pe || !c || Number(c.amount_due) <= 0} onClick={() => save('cn')}
          title="Locks the day, shows it to the client and creates a draft credit note in Zoho">{busy === 'cn' ? 'Creating…' : 'Save & create credit note'}</button>
        <button type="button" className="hq-btn" disabled={!!busy || !lease || !!pe} onClick={() => save('final')}>{busy === 'final' ? 'Saving…' : reportStatus === 'final' ? 'Save changes' : 'Save & finalize'}</button>
        {reportStatus !== 'final' && <button type="button" className="hq-signout" style={GHOST} disabled={!!busy || !lease} onClick={() => save('draft')}>{busy === 'draft' ? 'Saving…' : 'Save draft'}</button>}
        {onCancel && <button type="button" className="hq-signout" style={GHOST} onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}
function Row({ k, v, top }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, borderTop: top ? '1px solid var(--hq-line)' : 'none', paddingTop: top ? 6 : 0 }}>
      <span>{k}</span><span style={{ whiteSpace: 'nowrap' }}>{v}</span>
    </div>
  );
}

// ── lease page panel ─────────────────────────────────────────────────────────
export function PosLeasePanel({ leaseId, canManage, onChanged }) {
  const [pos, setPos] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [mode, setMode] = useState(null); // null | { kind:'new', initial } | { kind:'edit', report } | { kind:'drop' }
  const [open, setOpen] = useState('');
  const [loc, setLoc] = useState('');
  const [busy, setBusy] = useState('');

  const take = useCallback((p) => { setPos(p); setLoc(p.lease.pos_location || ''); }, []);
  const load = useCallback(async () => {
    const r = await apiFetch(`/api/equipment/leases/${leaseId}/pos`);
    const d = await readJson(r);
    if (r.ok) { take(d.pos); setErr(''); } else setErr(d.error || 'Could not load POS reports.');
  }, [leaseId, take]);
  useEffect(() => { load(); }, [load]);

  async function act(method, path, body, label) {
    setBusy(label); setMsg(''); setErr('');
    const r = await apiFetch('/api/equipment' + path, { method, headers: body ? JSON_HEADERS : undefined, body: body ? JSON.stringify(body) : undefined });
    const d = await readJson(r);
    setBusy('');
    if (d.pos) take(d.pos);
    if (!r.ok) { setErr(d.error || 'Something went wrong.'); return null; }
    if (d.message) setMsg(d.message);
    onChanged && onChanged();
    return d;
  }
  function lastStop() {
    const done = (pos.reports || []).filter((x) => x.tax_state || x.tax_rate);
    const last = done[done.length - 1];
    return last ? { tax_state: last.tax_state || '', tax_rate: last.tax_rate ? String(last.tax_rate) : '' } : {};
  }

  if (err && !pos) return <div className="hq-panel" style={{ marginBottom: 20 }}><div className="hq-panel-head"><h2>POS sales</h2></div><p className="hq-msg" style={{ padding: '0 20px 16px' }}>{err}</p></div>;
  if (!pos) return null;
  const t = pos.totals;
  const L0 = pos.lease;

  return (
    <>
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head" style={{ flexWrap: 'wrap', gap: 10 }}>
          <h2>POS sales</h2>
          <span style={MUTED}>Event service fee {L0.service_fee_pct}% (accepted terms){!L0.zoho_linked && ' · client not linked to Zoho — credit notes can’t be created'}
            {L0.zoho_linked && L0.cn_account && <> · Credit notes post to <Link to={'/clients/' + L0.client_id + '?tab=overview'} style={LINK} title="Change on the client profile › Zoho Books">{L0.cn_account}</Link></>}{/* @@CN_ACCOUNT_v108@@ */}</span>
        </div>
        <div style={{ padding: '14px 20px', display: 'grid', gap: 14 }}>
          {canManage && (
            <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}
              onSubmit={(e) => { e.preventDefault(); act('PUT', `/leases/${leaseId}/pos-location`, { pos_location: loc }, 'loc'); }}>
              <label style={{ flex: '0 1 280px' }}><span style={LABEL}>Square location name (matches the email subject)</span>
                <input className="hq-input" style={FULL} value={loc} onChange={(e) => setLoc(e.target.value)} maxLength={120} placeholder="Goblin" /></label>
              <button className="hq-signout" style={GHOST} type="submit" disabled={!!busy || loc === (L0.pos_location || '')}>Save</button>
            </form>
          )}
          <div className="hq-cards" style={{ marginBottom: 0 }}>
            <div className="hq-stat"><div className="hq-k">Reports (final)</div><div className="hq-v">{t.days}</div></div>
            <div className="hq-stat"><div className="hq-k">Net sales</div><div className="hq-v">{money(t.net_sales)}</div></div>
            <div className="hq-stat"><div className="hq-k">Due to client</div><div className="hq-v">{money(t.amount_due)}</div></div>
            <div className="hq-stat"><div className="hq-k">Credited in Zoho</div><div className="hq-v">{money(t.credited)}</div></div>
          </div>
          {msg && <p className="hq-msg" style={{ margin: 0 }}>{msg}</p>}
          {err && <p className="hq-msg" style={{ margin: 0, color: '#ff7a73' }}>{err}</p>}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="hq-table" style={{ minWidth: 900 }}>
            <thead><tr><th>Date</th><th style={{ textAlign: 'right' }}>Net sales</th><th style={{ textAlign: 'right' }}>Card</th><th style={{ textAlign: 'right' }}>Cash</th><th style={{ textAlign: 'right' }}>Tax</th><th style={{ textAlign: 'right' }}>Card fees</th><th style={{ textAlign: 'right' }}>Service fee</th><th style={{ textAlign: 'right' }}>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {pos.reports.map((r) => (
                <React.Fragment key={r.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setOpen(open === r.id ? '' : r.id)}>
                    <td style={{ whiteSpace: 'nowrap' }}>{span(r)}<div style={MUTED}>{r.location_name || ''}{r.tax_state ? ` · ${r.tax_state} ${r.tax_rate}%` : ''}</div></td>
                    <td style={{ textAlign: 'right' }}>{money(r.net_sales)}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.card_collected != null ? r.card_collected : r.total_collected)}</td>
                    <td style={{ textAlign: 'right' }}>{money(Number(r.cash_collected || 0) + Number(r.other_collected || 0))}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.tax_deducted)}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.card_fees)}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.service_fee)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(r.amount_due)}</td>
                    <td><StatusCell r={r} /></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canManage && <ReportActions r={r} zohoLinked={L0.zoho_linked} busy={busy} act={act}
                        onEdit={(rep0, voided) => { setMsg(''); setMode({ kind: 'edit', report: rep0, voided }); }} />}
                    </td>
                  </tr>
                  {open === r.id && (
                    <tr><td colSpan={10} style={{ background: 'var(--hq-field)' }}>
                      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', fontSize: 13 }}>
                        <div><span style={LABEL}>Gross / returns / discounts</span>{money(r.gross_sales)} / {money(r.returns)} / {money(r.discounts)}</div>
                        <div><span style={LABEL}>Total collected / tips</span>{money(r.total_collected)} / {money(r.tips)}</div>
                        <div><span style={LABEL}>Card sales (fee base share)</span>{money(r.card_sales)}</div>
                        <div><span style={LABEL}>Items / customers</span>{r.items_sold ?? '—'} / {r.customers ?? '—'}</div>
                        <div><span style={LABEL}>Service fee base</span>{money(r.service_fee_base)} × {r.service_fee_pct}%</div>
                        <div><span style={LABEL}>Source</span>{r.source === 'csv' ? 'Square CSV' : r.source === 'email' ? 'Square email' : 'Manual'} · {fmtTs(r.updated_at)}</div>
                      </div>
                      {r.fee_detail && <div style={{ ...MUTED, marginTop: 8 }}>Square fee rates: {r.fee_detail}</div>}
                      {r.top_items.length > 0 && <div style={{ ...MUTED, marginTop: 4 }}>Top sellers: {r.top_items.map((x) => `${x.name} ×${x.qty} (${money(x.sales)})`).join(' · ')}</div>}
                      {r.client_note && <div style={{ marginTop: 6, fontSize: 13 }}>To client: {r.client_note}</div>}
                      {r.notes && <div style={{ ...MUTED, marginTop: 4 }}>Internal: {r.notes}</div>}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
              {pos.reports.length === 0 && <tr><td className="hq-empty" colSpan={10}>No POS days yet. Drop the first Square Sales summary CSV below.</td></tr>}
            </tbody>
          </table>
        </div>
        {canManage && L0.accepted && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--hq-line)' }}>
            {!mode && <button className="hq-btn" onClick={() => setMode({ kind: 'drop' })}>Add sales (day or period)</button>}
            {mode && mode.kind === 'drop' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <PosEmailDrop compact onParsed={(d) => setMode({ kind: 'new', initial: { ...dayFromFields(d.parse.fields, d.parse.format === 'csv' ? 'csv' : 'email'), ...lastStop() } })} />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="hq-signout" style={GHOST} onClick={() => setMode({ kind: 'new', initial: { ...BLANK_DAY, location_name: L0.pos_location || '', ...lastStop() } })}>Enter by hand</button>
                  <button className="hq-signout" style={GHOST} onClick={() => setMode(null)}>Cancel</button>
                </div>
              </div>
            )}
            {mode && (mode.kind === 'new' || mode.kind === 'edit') && (
              <PosDayForm key={mode.kind === 'edit' ? mode.report.id : 'new'} leaseId={leaseId} leaseLocation={L0.pos_location}
                initial={mode.kind === 'edit' ? dayFromFields(mode.report, mode.report.source) : mode.initial}
                reportId={mode.kind === 'edit' ? mode.report.id : null}
                reportStatus={mode.kind === 'edit' ? mode.report.status : null} voided={mode.kind === 'edit' && mode.voided}
                onDone={(p, m, _l, failed) => { if (p) take(p); if (!failed) { setMode(null); setMsg(m || ''); onChanged && onChanged(); } }}
                onCancel={() => setMode(null)} />
            )}
          </div>
        )}
      </div>
      <SettlementPanel pos={pos} canManage={canManage} act={act} busy={busy} leaseId={leaseId} />
    </>
  );
}

// ── settlement (charges -> draft Zoho invoice) ───────────────────────────────
const KIND_LABEL = { rental_fee: 'Rental fee', lost: 'Lost unit', damaged: 'Damaged unit', negative_day: 'Negative day', other: 'Other' };
function SettlementPanel({ pos, canManage, act, busy, leaseId }) {
  const s = pos.settlement;
  const issued = s && s.status === 'issued';
  const [lines, setLines] = useState(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    setLines(s ? s.lines.map((l) => ({ ...l })) : null);
    setNote(s ? s.client_note || '' : '');
  }, [s && s.updated_at, s && s.status]);

  const editing = lines || [];
  const total = editing.reduce((a, l) => a + (Number(l.amount) || 0), 0);
  const due = Number(pos.totals.amount_due) || 0;
  const setLine = (i, k) => (e) => setLines(editing.map((l, j) => (j === i ? { ...l, [k]: e.target.value } : l)));
  const editable = canManage && !issued;
  const unsaved = JSON.stringify(editing) !== JSON.stringify(s ? s.lines : []) || note !== ((s && s.client_note) || '');
  const missingCn = pos.reports.filter((r) => r.status === 'final' && !r.cn_status && Number(r.amount_due) > 0).length;

  return (
    <div className="hq-panel" style={{ marginBottom: 20 }}>
      <div className="hq-panel-head" style={{ flexWrap: 'wrap', gap: 10 }}>
        <h2>Settlement</h2>
        {issued ? <span style={pill('green')}>Issued {fmtTs(s.issued_at)}</span> : s ? <span style={pill('grey')}>Draft — client can’t see it</span> : <span style={MUTED}>Charges at the end of the rental, invoiced in Zoho</span>}
      </div>
      <div style={{ padding: 20, display: 'grid', gap: 14 }}>
        {editable && (!lines || lines.length === 0) && pos.suggested_lines.length > 0 && (
          <div><button type="button" className="hq-signout" style={GHOST} onClick={() => setLines(pos.suggested_lines.map((l) => ({ ...l })))}>Load suggested charges ({pos.suggested_lines.length})</button>
            <span style={{ ...MUTED, marginLeft: 10 }}>Rental fee, lost/damaged units (replacement value — lower it to a repair cost if that applies) and any negative days.</span></div>
        )}
        <div style={{ display: 'grid', gap: 8 }}>
          {editing.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ ...pill('grey'), flex: '0 0 auto' }}>{KIND_LABEL[l.kind] || 'Other'}</span>
              <input className="hq-input" style={{ flex: '3 1 300px', minWidth: 0 }} value={l.label} onChange={setLine(i, 'label')} disabled={!editable} aria-label="Description" maxLength={160} />
              <input className="hq-input" style={{ flex: '0 1 130px', minWidth: 0, textAlign: 'right' }} inputMode="decimal" value={l.amount} onChange={setLine(i, 'amount')} disabled={!editable} aria-label="Amount" />
              {editable && <button type="button" className="hq-signout" style={GHOST} onClick={() => setLines(editing.filter((_, j) => j !== i))}>Remove</button>}
            </div>
          ))}
          {editing.length === 0 && <div style={MUTED}>No charges{issued ? '.' : ' yet.'}</div>}
          {editable && <div><button type="button" className="hq-signout" style={GHOST} onClick={() => setLines([...editing, { kind: 'other', label: '', amount: '' }])}>Add a charge</button></div>}
        </div>
        <label><span style={LABEL}>Note to the client</span>
          <input className="hq-input" style={FULL} value={note} onChange={(e) => setNote(e.target.value)} disabled={!editable} maxLength={1000} placeholder="Shown with the settlement in their portal and on the invoice" /></label>
        <div style={{ display: 'grid', gap: 4, fontSize: 14, maxWidth: 460 }}>
          <Row k="Due to client from POS days" v={money(due)} />
          <Row k="Less lease charges" v={'− ' + money(total)} />
          <Row k={<strong>Net settlement to client</strong>} v={<strong>{money(due - total)}</strong>} top />
          {s && s.inv_number && <div style={MUTED}>Zoho invoice {s.inv_number} (draft) · {money(s.inv_total)}</div>}
          {missingCn > 0 && <div style={{ color: '#e8c15a', fontSize: 13 }}>{missingCn} final day{missingCn === 1 ? ' has' : 's have'} no credit note yet.</div>}
          {pos.lease.units_out > 0 && <div style={{ color: '#e8c15a', fontSize: 13 }}>{pos.lease.units_out} unit{pos.lease.units_out === 1 ? ' is' : 's are'} still out — check them in before settling.</div>}
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {editable && <button className="hq-btn" disabled={!!busy || !unsaved} onClick={() => act('PUT', `/leases/${leaseId}/settlement`, { lines: editing, client_note: note }, 'set')}>{busy === 'set' ? 'Saving…' : 'Save settlement'}</button>}
            {editable && s && <button className="hq-btn" style={GREEN_BTN} disabled={!!busy || unsaved}
              onClick={() => { if (window.confirm(total > 0 ? `Issue the settlement? This creates a draft Zoho invoice for ${money(total)} and shows the settlement to the client.` : 'Issue the settlement with no charges? The client will see it in their portal.')) act('POST', `/leases/${leaseId}/settlement/issue`, {}, 'issue'); }}>
              {busy === 'issue' ? 'Issuing…' : total > 0 ? 'Issue + create Zoho invoice' : 'Issue settlement'}</button>}
            {issued && !s.inv_status && <button className="hq-signout" style={GHOST} disabled={!!busy} onClick={() => act('POST', `/leases/${leaseId}/settlement/reopen`, {}, 'sreopen')}>Reopen</button>}
            {s && (s.inv_status === 'created' || s.inv_stuck) && <button className="hq-signout" style={GHOST} disabled={!!busy}
              onClick={() => { if (window.confirm('Only do this after voiding/deleting the invoice in Zoho. Unlink it and put the settlement back to draft?')) act('POST', `/leases/${leaseId}/settlement/invoice/reset`, {}, 'ireset'); }}>Unlink invoice</button>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Equipment › POS sales tab: drop an email, match the lease, save ──────────
export function PosInbox({ canManage }) {
  const [parsed, setParsed] = useState(null);
  const [recent, setRecent] = useState(null);
  const [msg, setMsg] = useState(null);
  const [days, setDays] = useState(30);
  const [editing, setEditing] = useState(null); // { report, voided }   @@POS_EDIT_v104_2@@
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    const r = await apiFetch('/api/equipment/pos/reports?days=' + days);
    const d = await readJson(r);
    setRecent(r.ok ? d.reports || [] : []);
    if (!r.ok) setMsg({ text: d.error || 'Could not load POS reports.' });
  }, [days]);
  useEffect(() => { load(); }, [load]);

  async function act(method, path, body, label) {
    setBusy(label); setMsg(null);
    const r = await apiFetch('/api/equipment' + path, { method, headers: body ? JSON_HEADERS : undefined, body: body ? JSON.stringify(body) : undefined });
    const d = await readJson(r);
    setBusy('');
    setMsg({ text: r.ok ? (d.message || (method === 'DELETE' ? 'Report deleted.' : 'Done.')) : (d.error || 'Something went wrong.') });
    load();
    return r.ok ? d : null;
  }
  const options = parsed ? [...parsed.leases].sort((a, b) => (b.in_period - a.in_period) || (b.location_match - a.location_match)) : [];
  return (
    <>
      {canManage && (
        <div className="hq-panel" style={{ marginBottom: 16 }}>
          <div className="hq-panel-head"><h2>Add sales from Square</h2>
            {parsed && <button className="hq-signout" style={GHOST} onClick={() => setParsed(null)}>Start over</button>}</div>
          <div style={{ padding: 20, display: 'grid', gap: 14 }}>
            {msg && <p className="hq-msg" style={{ margin: 0 }}>{msg.text}{msg.leaseId && <> · <Link to={'/equipment/leases/' + msg.leaseId} style={LINK}>Open the lease</Link></>}</p>}
            {editing && (
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ fontWeight: 600 }}>Editing {span(editing.report)} · {editing.report.client_name} · {editing.report.lease_number}</div>
                <PosDayForm key={editing.report.id + (editing.voided ? ':v' : '')} leaseId={editing.report.lease_id} reportId={editing.report.id}
                  reportStatus={editing.report.status} voided={editing.voided}
                  initial={dayFromFields(editing.report, editing.report.source)}
                  onDone={(p, m, leaseId, failed) => { if (!failed) { setEditing(null); setMsg({ text: m, leaseId }); load(); } }}
                  onCancel={() => setEditing(null)} />
              </div>
            )}
            {!parsed && !editing && <PosEmailDrop onParsed={(d) => { setMsg(null); setParsed(d); }} />}
            {parsed && (
              <>
                <div style={MUTED}>{parsed.parse.subject || 'Pasted text'}{parsed.parse.missing.length ? ` · not found in the email: ${parsed.parse.missing.join(', ').replace(/_/g, ' ')}` : ''}</div>
                {parsed.parse.warnings.map((w) => <div key={w} style={{ color: '#e8c15a', fontSize: 13 }}>{w}</div>)}
                {!parsed.suggested_lease_id && <div style={{ color: '#e8c15a', fontSize: 13 }}>
                  {options.some((o) => o.in_period) ? 'More than one lease covers this date — pick the right one (set its Square location so next time it matches automatically).' : 'No accepted lease covers this date. Check the lease’s rental period.'}</div>}
                {parsed.existing && <div style={{ color: '#e8c15a', fontSize: 13 }}>This lease already has a report covering {span(parsed.existing)} ({parsed.existing.status}). <Link to={'/equipment/leases/' + parsed.suggested_lease_id} style={LINK}>Open the lease</Link> to edit it.</div>}
                <PosDayForm leaseId={null} leaseOptions={options} initialLease={parsed.suggested_lease_id}
                  initial={dayFromFields(parsed.parse.fields, parsed.parse.format === 'csv' ? 'csv' : 'email')}
                  key={(parsed.parse.subject || '') + (parsed.suggested_lease_id || '')}
                  onDone={(p, m, leaseId, failed) => { if (!failed) { setParsed(null); setMsg({ text: m, leaseId }); load(); } }}
                  onCancel={() => setParsed(null)} />
              </>
            )}
          </div>
        </div>
      )}
      <div className="hq-panel">
        <div className="hq-panel-head" style={{ flexWrap: 'wrap', gap: 10 }}>
          <h2>Recent POS reports</h2>
          <select className="hq-select" aria-label="Period" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last year</option>
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="hq-table" style={{ minWidth: 900 }}>
            <thead><tr><th>Date</th><th>Client</th><th>Lease</th><th style={{ textAlign: 'right' }}>Net sales</th><th style={{ textAlign: 'right' }}>Due to client</th><th>Status</th>{canManage && <th></th>}</tr></thead>
            <tbody>
              {(recent || []).map((r) => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{span(r)}<div style={MUTED}>{r.location_name || ''}</div></td>
                  <td>{r.client_name}</td>
                  <td style={{ ...MONO, fontSize: 13, whiteSpace: 'nowrap' }}><Link to={'/equipment/leases/' + r.lease_id} style={LINK}>{r.lease_number}</Link></td>
                  <td style={{ textAlign: 'right' }}>{money(r.net_sales)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(r.amount_due)}</td>
                  <td><StatusCell r={r} /></td>
                  {canManage && <td><ReportActions r={r} zohoLinked={r.zoho_linked} busy={busy} act={act}
                    onEdit={(rep0, voided) => { setParsed(null); setMsg(null); setEditing({ report: rep0, voided }); window.scrollTo({ top: 0, behavior: 'smooth' }); }} /></td>}
                </tr>
              ))}
              {recent && recent.length === 0 && <tr><td className="hq-empty" colSpan={7}>No POS days in this period.</td></tr>}
              {recent === null && <tr><td className="hq-empty" colSpan={7}>Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
